/**
 * /portal/register — self-serve signup. Two modes: Organization (team) vs Personal (solo).
 * Both grant 20 free credits to the wallet.
 */
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, User as UserIcon, Mail, Lock, ArrowRight, CheckCircle2, Sparkles } from 'lucide-react';
import { usePortalStore, toast } from '../../stores';
import { onboardingApi } from '../../services/onboarding';
import { NexusButton } from '../../components/nexus-ui/NexusButton';
import { NexusInput } from '../../components/nexus-ui/NexusInput';
import { NexusCard } from '../../components/nexus-ui/NexusCard';
import { CognaraLogo } from '../../components/instrument/CognaraLogo';

const INDUSTRIES = ['Consulting', 'EPC / Construction', 'Manufacturing', 'Technology', 'Financial Services', 'Healthcare', 'Energy', 'Retail', 'Education', 'Public Sector', 'Other'];

const PortalRegister = () => {
  const [mode, setMode] = useState('organization'); // 'organization' | 'personal'
  const [form, setForm] = useState({ org_name: '', industry: 'Consulting', name: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const { setAuth, isAuthenticated } = usePortalStore();
  const navigate = useNavigate();

  useEffect(() => { if (isAuthenticated) navigate('/portal/dashboard', { replace: true }); }, [isAuthenticated, navigate]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const { data } = mode === 'personal'
        ? await onboardingApi.personalSignup({ name: form.name, email: form.email, password: form.password })
        : await onboardingApi.orgSignup(form);
      setAuth({ token: data.access_token, user: data.user, tenant: data.tenant, authType: 'jwt' });
      setDone(true);
      const accountLabel = mode === 'personal' ? 'personal account' : data.tenant.name;
      toast.success(`${accountLabel} is live — 20 free credits added`);
      setTimeout(() => navigate('/portal/dashboard', { replace: true }), 1800);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not complete signup');
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--nexus-bg-primary)' }}>
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center">
          <div className="inline-flex w-16 h-16 rounded-full items-center justify-center mb-4"
            style={{ background: 'rgba(110,231,183,0.15)' }}>
            <CheckCircle2 size={32} style={{ color: '#10B981' }} />
          </div>
          <h2 className="font-heading text-2xl font-semibold text-nexus-text-primary">Welcome to Cognara</h2>
          <p className="text-[13px] text-nexus-text-secondary mt-1">Taking you to your workspace…</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--nexus-bg-primary)' }}>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md z-10">
        <NexusCard variant="glass" hover={false} className="p-8">
          <div className="text-center mb-6">
            <div className="inline-flex w-14 h-14 rounded-2xl items-center justify-center mb-3"
              style={{ background: 'var(--layer-2)', border: '1px solid var(--hairline)', color: 'var(--nexus-text-primary)' }}>
              <CognaraLogo variant="icon" size={28} />
            </div>
            <h1 className="font-heading text-2xl font-semibold text-nexus-text-primary">Create your account</h1>
            <p className="text-[13px] text-nexus-text-secondary mt-1.5">
              <span className="font-semibold text-nexus-accent-orange">20 free credits</span> on signup. No credit card required.
            </p>
          </div>

          {/* Account type toggle */}
          <div className="grid grid-cols-2 gap-2 mb-5">
            <button type="button" onClick={() => setMode('personal')}
              className={`p-3 rounded-lg text-left transition-all ${mode === 'personal' ? 'ring-2' : ''}`}
              style={{
                background: 'var(--layer-2)', border: '1px solid var(--hairline)',
                boxShadow: mode === 'personal' ? '0 0 0 2px #F26B1F' : 'none',
              }}
              data-testid="signup-mode-personal">
              <UserIcon size={14} style={{ color: '#F26B1F' }} />
              <div className="text-[13px] font-semibold text-nexus-text-primary mt-1.5">Personal</div>
              <div className="text-[11px] text-nexus-text-tertiary leading-tight">Just for me, no team</div>
            </button>
            <button type="button" onClick={() => setMode('organization')}
              className={`p-3 rounded-lg text-left transition-all ${mode === 'organization' ? 'ring-2' : ''}`}
              style={{
                background: 'var(--layer-2)', border: '1px solid var(--hairline)',
                boxShadow: mode === 'organization' ? '0 0 0 2px #F26B1F' : 'none',
              }}
              data-testid="signup-mode-organization">
              <Building2 size={14} style={{ color: '#6BA6F5' }} />
              <div className="text-[13px] font-semibold text-nexus-text-primary mt-1.5">Organization</div>
              <div className="text-[11px] text-nexus-text-tertiary leading-tight">Team + shared credits</div>
            </button>
          </div>

          <form onSubmit={onSubmit} className="space-y-3">
            {mode === 'organization' && (
              <>
                <NexusInput label="Organization name" value={form.org_name} onChange={update('org_name')}
                  placeholder="Acme Consulting" icon={<Building2 size={14} />} required data-testid="reg-org-name" autoFocus />
                <div>
                  <label className="block text-[11px] uppercase tracking-wider font-semibold mb-1.5 text-nexus-text-tertiary">Industry</label>
                  <select value={form.industry} onChange={update('industry')}
                    className="w-full px-3 py-2.5 rounded-lg text-[13px] outline-none"
                    style={{ background: 'var(--layer-2)', border: '1px solid var(--hairline)', color: 'var(--nexus-text-primary)' }}
                    data-testid="reg-industry">
                    {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
                  </select>
                </div>
              </>
            )}
            <NexusInput label="Your name" value={form.name} onChange={update('name')}
              placeholder={mode === 'personal' ? 'Priya Sharma' : 'Jane Founder'}
              icon={<UserIcon size={14} />} required data-testid="reg-name"
              autoFocus={mode === 'personal'} />
            <NexusInput label="Work email" type="email" value={form.email} onChange={update('email')}
              placeholder="you@example.com" icon={<Mail size={14} />} required data-testid="reg-email" />
            <NexusInput label="Password" type="password" value={form.password} onChange={update('password')}
              placeholder="At least 8 characters" icon={<Lock size={14} />} error={error} required minLength={8}
              data-testid="reg-password" />
            <NexusButton type="submit" className="w-full" isLoading={loading} data-testid="reg-submit">
              <Sparkles size={14} /> {mode === 'personal' ? 'Create personal account' : 'Create organization'}
            </NexusButton>
          </form>

          <p className="text-[11px] text-nexus-text-tertiary text-center mt-5">
            By signing up you agree to Cognara's <Link to="/terms" className="underline">Terms</Link> &amp; <Link to="/privacy" className="underline">Privacy</Link>.
          </p>
          <div className="mt-5 pt-5 border-t text-center text-[12px] text-nexus-text-secondary" style={{ borderColor: 'var(--hairline)' }}>
            Already have an account?{' '}
            <Link to="/portal/login" className="font-semibold text-nexus-accent-blue inline-flex items-center gap-1" data-testid="go-login">
              Sign in <ArrowRight size={12} />
            </Link>
          </div>
        </NexusCard>
      </motion.div>
    </div>
  );
};

export default PortalRegister;
